import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { send, remove, pending, cachedJSON, newRef } from "./outbox";

const API = "/api";
const VEHICLE_TYPES = ["Traktor", "Dumperveoauto", "Poolhaagis"];
const GOLD = "#B8962E";
const DARK = "#1a1a14";
const MID = "#242418";
const RECENT_MS = 15 * 60 * 1000;

const today = () => new Date().toLocaleDateString("sv-SE");
const truckKey = t => t.reg + "_" + t.material;
const loadLocal = key => { try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; } };
const saveLocal = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

export default function Operator() {
  const navigate = useNavigate();
  const authCtx = useAuth();
  const [sites, setSites] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [operatorName, setOperatorName] = useState(() => {
    if (authCtx?.user?.name) return authCtx.user.name;
    try { return localStorage.getItem("op_name") || ""; } catch { return ""; }
  });
  const [nameInput, setNameInput] = useState("");
  const [selectedSite, setSelectedSite] = useState(() => {
    try { return localStorage.getItem("op_site") || ""; } catch { return ""; }
  });
  const [trucks, setTrucks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [regSuggestions, setRegSuggestions] = useState([]);
  const [newReg, setNewReg] = useState("");
  const [newType, setNewType] = useState("Dumperveoauto");
  const [newMaterial, setNewMaterial] = useState("");
  const [confirmTruck, setConfirmTruck] = useState(null);
  const [editingReg, setEditingReg] = useState(null);
  const [editRegValue, setEditRegValue] = useState("");
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("op_sort") || "alpha");
  const [customOrder, setCustomOrder] = useState(() => { try { return JSON.parse(localStorage.getItem("op_custom_order") || "{}"); } catch { return {}; } });
  const [dragIdx, setDragIdx] = useState(null);
  const [showSortBar, setShowSortBar] = useState(false);
  const dragOverIdx = useRef(null);
  const truckListRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem("op_name", operatorName); } catch {}
  }, [operatorName]);

  useEffect(() => {
    try { localStorage.setItem("op_site", selectedSite); } catch {}
  }, [selectedSite]);

  useEffect(() => {
    cachedJSON(`${API}/sites`).then(setSites).catch(() => {});
    cachedJSON(`${API}/materials`).then(setMaterials).catch(() => {});
  }, []);

  // Today's trucks and the refs of this phone's taps, kept locally so the screen works offline.
  const dayKey = `${selectedSite}:${operatorName}:${today()}`;
  const trucksKey = `op_trucks:${dayKey}`;
  const tapsKey = `op_taps:${dayKey}`;
  const [taps, setTaps] = useState({});

  useEffect(() => {
    if (!selectedSite || !operatorName) return;
    const local = loadLocal(trucksKey);
    if (local) setTrucks(local);
    setTaps(loadLocal(tapsKey) || {});
    // While taps are still waiting to be sent, the local counts are ahead of the server's.
    if (pending().some(i => i.kind === "delivery")) return;
    fetch(`${API}/deliveries/today?site=${selectedSite}&logged_by=${encodeURIComponent(operatorName)}`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(data => { setTrucks(data); saveLocal(trucksKey, data); })
      .catch(() => {});
  }, [selectedSite, operatorName, trucksKey, tapsKey]);

  useEffect(() => { if (selectedSite && operatorName) saveLocal(trucksKey, trucks); }, [trucks, trucksKey, selectedSite, operatorName]);
  useEffect(() => { if (selectedSite && operatorName) saveLocal(tapsKey, taps); }, [taps, tapsKey, selectedSite, operatorName]);

  const logLoad = (truck) => {
    const ref = newRef();
    const at = new Date().toISOString();
    setTrucks(prev => {
      const exists = prev.some(t => truckKey(t) === truckKey(truck));
      return exists ? prev.map(t => truckKey(t) === truckKey(truck) ? { ...t, loads: t.loads + 1 } : t)
                    : [...prev, { ...truck, loads: 1 }];
    });
    setTaps(prev => ({ ...prev, [truckKey(truck)]: [...(prev[truckKey(truck)] || []), { ref, at }] }));
    send(`${API}/deliveries`, {
      kind: "delivery", meta: { ref },
      body: { site: selectedSite, reg: truck.reg, vehicle_type: truck.vehicle_type, material: truck.material,
              logged_by: operatorName, client_ref: ref, logged_at: at },
    });
  };

  const handleTap = async (truck, skipCheck = false) => {
    if (!skipCheck) {
      // This phone's own taps answer offline; the server also knows other operators' taps.
      const mine = Object.entries(taps).filter(([k]) => k.startsWith(truck.reg + "_")).flatMap(([, v]) => v);
      const last = mine.length ? Math.max(...mine.map(t => Date.parse(t.at))) : 0;
      if (Date.now() - last < RECENT_MS) {
        setConfirmTruck({ truck, minutesAgo: Math.floor((Date.now() - last) / 60000) });
        return;
      }
      try {
        const res = await fetch(`${API}/deliveries/recent-check?site=${encodeURIComponent(selectedSite)}&reg=${encodeURIComponent(truck.reg)}`, { credentials: "include" });
        const check = res.ok ? await res.json() : {};
        if (check.recent) {
          setConfirmTruck({ truck, minutesAgo: check.minutes_ago });
          return;
        }
      } catch {}
    }
    logLoad(truck);
  };

  const orderKey = selectedSite || "_";
  const getSortedTrucks = () => {
    if (sortMode === "alpha") return [...trucks].sort((a, b) => a.reg.localeCompare(b.reg));
    if (sortMode === "alpha-rev") return [...trucks].sort((a, b) => b.reg.localeCompare(a.reg));
    const order = customOrder[orderKey] || [];
    return [...trucks].sort((a, b) => {
      const ia = order.indexOf(a.reg + "_" + a.material);
      const ib = order.indexOf(b.reg + "_" + b.material);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  };
  const sortedTrucks = getSortedTrucks();

  const handleSortChange = (mode) => {
    setSortMode(mode);
    localStorage.setItem("op_sort", mode);
    if (mode === "custom" && !customOrder[orderKey]) {
      const currentOrder = getSortedTrucks().map(t => t.reg + "_" + t.material);
      const next = { ...customOrder, [orderKey]: currentOrder };
      setCustomOrder(next);
      localStorage.setItem("op_custom_order", JSON.stringify(next));
    }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); dragOverIdx.current = idx; };
  const handleDragEnd = () => {
    if (dragIdx === null || dragOverIdx.current === null || dragIdx === dragOverIdx.current) { setDragIdx(null); return; }
    const items = [...sortedTrucks];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(dragOverIdx.current, 0, moved);
    const newOrder = items.map(t => t.reg + "_" + t.material);
    const next = { ...customOrder, [orderKey]: newOrder };
    setCustomOrder(next);
    localStorage.setItem("op_custom_order", JSON.stringify(next));
    setDragIdx(null);
    dragOverIdx.current = null;
  };

  const touchDragState = useRef({ idx: null, startY: 0, el: null });
  const handleTouchDragStart = (e, idx) => {
    const touch = e.touches[0];
    touchDragState.current = { idx, startY: touch.clientY, el: e.currentTarget };
    setDragIdx(idx);
  };
  const handleTouchDragMove = (e) => {
    if (touchDragState.current.idx === null) return;
    e.preventDefault();
    const touch = e.touches[0];
    const list = truckListRef.current;
    if (!list) return;
    const cards = list.querySelectorAll("[data-truck-idx]");
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
        dragOverIdx.current = parseInt(card.dataset.truckIdx);
        break;
      }
    }
  };
  const handleTouchDragEnd = () => {
    handleDragEnd();
    touchDragState.current = { idx: null, startY: 0, el: null };
  };

  const handleRegTap = (truck) => {
    setEditingReg(truck);
    setEditRegValue(truck.reg);
  };

  const handleRegUpdate = async () => {
    const newReg = editRegValue.trim().toUpperCase();
    if (!newReg || newReg === editingReg.reg) { setEditingReg(null); return; }
    const oldReg = editingReg.reg;
    send(`${API}/deliveries/update-reg`, { method: "PATCH", kind: "update-reg",
      body: { old_reg: oldReg, new_reg: newReg, site: selectedSite } });
    setTrucks(prev => prev.map(t => t.reg === oldReg ? { ...t, reg: newReg } : t));
    setTaps(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) =>
      [k.startsWith(oldReg + "_") ? newReg + k.slice(oldReg.length) : k, v])));
    setEditingReg(null);
  };

  const handleDecrement = async (truck) => {
    if (truck.loads <= 0) return;
    const mine = taps[truckKey(truck)] || [];
    const last = mine[mine.length - 1];
    setTrucks(prev => prev.map(t => truckKey(t) === truckKey(truck) ? { ...t, loads: Math.max(0, t.loads - 1) } : t));
    if (last) {
      setTaps(prev => ({ ...prev, [truckKey(truck)]: mine.slice(0, -1) }));
      // Not sent yet: just take it back out of the outbox.
      if (remove(i => i.meta?.ref === last.ref)) return;
      send(`${API}/deliveries/by-ref/${last.ref}`, { method: "DELETE", kind: "undo" });
    } else {
      send(`${API}/deliveries/last?site=${encodeURIComponent(selectedSite)}&reg=${encodeURIComponent(truck.reg)}`, { method: "DELETE", kind: "undo" });
    }
  };

  const handleAddTruck = async () => {
    if (!newReg.trim() || !newMaterial) return;
    const reg = newReg.trim().toUpperCase();
    const existing = trucks.find(t => t.reg === reg && t.material === newMaterial);
    if (existing) { handleTap(existing); setShowModal(false); setNewReg(""); return; }
    setShowModal(false);
    setNewReg("");
    logLoad({ reg, vehicle_type: newType, material: newMaterial });
  };

  if (!operatorName) return (
    <div style={{ background: DARK, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32 }}>
      <button onClick={() => navigate("/")} style={{ position: "absolute", top: 20, left: 20, background: "none", border: "none", color: "#555", fontSize: 13, cursor: "pointer" }}>← Tagasi</button>
      <div style={{ color: GOLD, fontWeight: 800, fontSize: 28, letterSpacing: 3, marginBottom: 8 }}>STEINMANN OÜ</div>
      <div style={{ color: "#888", fontSize: 13, letterSpacing: 2, marginBottom: 48 }}>SISESTA OMA NIMI</div>
      <input value={nameInput} onChange={e => setNameInput(e.target.value)}
        onKeyDown={e => e.key === "Enter" && nameInput.trim() && setOperatorName(nameInput.trim().replace(/\b\w/g, c => c.toUpperCase()))}
        placeholder="Ees- ja perekonnanimi" autoFocus
        style={{ background: MID, border: `1px solid ${GOLD}`, borderRadius: 12, padding: "16px 24px", color: "#fff", fontSize: 20, fontWeight: 600, width: 280, textAlign: "center", fontFamily: "sans-serif", outline: "none", marginBottom: 16, boxSizing: "border-box" }} />
      <button onClick={() => nameInput.trim() && setOperatorName(nameInput.trim().replace(/\b\w/g, c => c.toUpperCase()))}
        disabled={!nameInput.trim()}
        style={{ background: nameInput.trim() ? GOLD : "#333", border: "none", borderRadius: 12, color: nameInput.trim() ? "#fff" : "#666", fontWeight: 800, fontSize: 18, padding: "16px", width: 280, cursor: nameInput.trim() ? "pointer" : "not-allowed", letterSpacing: 1 }}>Edasi →</button>
    </div>
  );

  if (!selectedSite) return (
    <div style={{ background: DARK, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32 }}>
      <button onClick={() => setOperatorName("")} style={{ position: "absolute", top: 20, left: 20, background: "none", border: "none", color: "#555", fontSize: 13, cursor: "pointer" }}>← Tagasi</button>
      <div style={{ color: GOLD, fontWeight: 800, fontSize: 28, letterSpacing: 3, marginBottom: 8 }}>STEINMANN OÜ</div>
      <div style={{ color: "#888", fontSize: 13, letterSpacing: 2, marginBottom: 4 }}>VALI OBJEKT</div>
      <div style={{ color: "#555", fontSize: 12, marginBottom: 40 }}>{operatorName}</div>
      {sites.map(s => (
        <button key={s.id} onClick={() => setSelectedSite(s.name)} style={{ background: GOLD, border: "none", borderRadius: 12, color: "#fff", fontWeight: 800, fontSize: 24, padding: "20px 60px", margin: 10, cursor: "pointer", width: 280, letterSpacing: 1 }}>{s.name}</button>
      ))}
    </div>
  );

  const totalLoads = trucks.reduce((s, t) => s + t.loads, 0);

  return (
    <div style={{ background: DARK, minHeight: "100vh", color: "#fff", fontFamily: "sans-serif" }}>
      <div style={{ background: "#111", borderBottom: `2px solid ${GOLD}`, padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <span style={{ color: GOLD, fontWeight: 800, fontSize: 16, letterSpacing: 2 }}>STEINMANN OÜ</span>
          <span style={{ color: "#555", margin: "0 10px" }}>|</span>
          <span style={{ color: "#fff", fontWeight: 700 }}>{selectedSite}</span>
          <span style={{ color: "#555", margin: "0 10px" }}>|</span>
          <span style={{ color: "#888", fontSize: 13 }}>{operatorName}</span>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ color: GOLD, fontWeight: 800, fontSize: 22 }}>{totalLoads}</div>
          <div style={{ color: "#555", fontSize: 10, letterSpacing: 1 }}>KOORMAT TÄNA</div>
        </div>
      </div>
      {trucks.length > 1 && (
        <div style={{ background: "#111" }}>
          <button onClick={() => setShowSortBar(prev => !prev)} style={{
            width: "100%", padding: "8px 16px", background: "transparent", border: "none", borderBottom: showSortBar ? "1px solid #333" : "none",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer",
          }}>
            <span style={{ fontSize: 14, color: showSortBar ? GOLD : "#555" }}>⇅</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: showSortBar ? GOLD : "#555", letterSpacing: 1 }}>Sorteeri</span>
          </button>
          {showSortBar && (
            <div style={{ display: "flex", gap: 6, padding: "8px 16px" }}>
              {[["alpha", "A→Z"], ["alpha-rev", "Z→A"], ["custom", "Kohandatud"]].map(([mode, label]) => (
                <button key={mode} onClick={() => handleSortChange(mode)} style={{
                  flex: 1, padding: "8px 4px", borderRadius: 8, fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
                  border: `1px solid ${sortMode === mode ? GOLD : "#333"}`,
                  background: sortMode === mode ? "#2a2410" : "transparent",
                  color: sortMode === mode ? GOLD : "#666", cursor: "pointer",
                }}>{label}</button>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ padding: "16px 16px 100px" }} ref={truckListRef}>
        {trucks.length === 0 && <div style={{ textAlign: "center", color: "#555", marginTop: 60, fontSize: 14 }}>Ühtegi sõidukit ei ole registreeritud.<br />Lisa esimene sõiduk allpool.</div>}
        {sortedTrucks.map((truck, idx) => (
          <div key={truck.reg + "_" + truck.material} data-truck-idx={idx}
            draggable={showSortBar && sortMode === "custom"}
            onDragStart={showSortBar && sortMode === "custom" ? () => handleDragStart(idx) : undefined}
            onDragOver={showSortBar && sortMode === "custom" ? e => handleDragOver(e, idx) : undefined}
            onDragEnd={showSortBar && sortMode === "custom" ? handleDragEnd : undefined}
            onTouchStart={showSortBar && sortMode === "custom" ? e => handleTouchDragStart(e, idx) : undefined}
            onTouchMove={showSortBar && sortMode === "custom" ? handleTouchDragMove : undefined}
            onTouchEnd={showSortBar && sortMode === "custom" ? handleTouchDragEnd : undefined}
            style={{
              background: MID, border: `1px solid ${dragIdx === idx ? GOLD : "#333"}`, borderRadius: 12, padding: "16px 20px", marginBottom: 12,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              opacity: dragIdx === idx ? 0.5 : 1, transition: "opacity 0.15s",
            }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {showSortBar && sortMode === "custom" && <div style={{ color: "#555", fontSize: 18, cursor: "grab", touchAction: "none", padding: "4px" }}>☰</div>}
              <div>
                <div
                  onClick={() => handleRegTap(truck)}
                  style={{ fontWeight: 800, fontSize: 20, letterSpacing: 2, color: "#fff", cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 4 }}>{truck.reg}</div>
                <div style={{ marginTop: 2 }}><span style={{ color: "#888", fontSize: 12 }}>{truck.vehicle_type} · </span><span style={{ color: "#c8a84b", fontSize: 14, fontWeight: 700 }}>{truck.material}</span></div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={() => handleDecrement(truck)} disabled={truck.loads === 0} style={{ background: truck.loads === 0 ? "#2a2a20" : "#5a3a1a", border: "none", borderRadius: 8, width: 44, height: 44, fontSize: 22, color: truck.loads === 0 ? "#444" : GOLD, cursor: truck.loads === 0 ? "not-allowed" : "pointer", fontWeight: 800 }}>−</button>
              <div style={{ textAlign: "center", minWidth: 48 }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: GOLD }}>{truck.loads}</div>
                <div style={{ fontSize: 10, color: "#555", letterSpacing: 1 }}>KOORMAT</div>
              </div>
              <button onClick={() => handleTap(truck)} style={{ background: GOLD, border: "none", borderRadius: 10, width: 64, height: 64, fontSize: 32, color: "#fff", cursor: "pointer", fontWeight: 800 }}>+</button>
            </div>
          </div>
        ))}
      </div>
      <div style={{ position: "fixed", bottom: "var(--shell-bottom, 0px)", left: "var(--shell-left, 0px)", right: 0, zIndex: 40, padding: "16px 20px", background: "#111", borderTop: "1px solid #333" }}>
        <button onClick={() => {
          setShowModal(true);
          cachedJSON(`${API}/reg-suggestions${selectedSite ? "?site=" + encodeURIComponent(selectedSite) : ""}`)
            .then(setRegSuggestions).catch(() => {});
        }} style={{ width: "100%", background: "#1E6B2E", border: "none", borderRadius: 12, color: "#fff", fontWeight: 800, fontSize: 18, padding: "16px", cursor: "pointer", letterSpacing: 1 }}>+ Lisa sõiduk</button>
      </div>
      {confirmTruck && (
        <div style={{ position: "fixed", inset: 0, background: "#000000cc", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 100 }}>
          <div style={{ background: MID, border: `1px solid #c0392b`, borderRadius: 16, padding: 28, width: "100%", maxWidth: 360, textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#fff", marginBottom: 8 }}>
              {confirmTruck.truck.reg} juba logitud
            </div>
            <div style={{ color: "#aaa", fontSize: 14, marginBottom: 24 }}>
              {confirmTruck.minutesAgo === 0
                ? "Alla minuti tagasi"
                : `${confirmTruck.minutesAgo} min tagasi`} · Kas oled kindel?
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmTruck(null)}
                style={{ flex: 1, padding: "14px", background: "#333", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                Tühista
              </button>
              <button onClick={() => { handleTap(confirmTruck.truck, true); setConfirmTruck(null); }}
                style={{ flex: 2, padding: "14px", background: "#c0392b", border: "none", borderRadius: 10, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 15 }}>
                Jah, lisa koorem
              </button>
            </div>
          </div>
        </div>
      )}
      {editingReg && (
        <div style={{ position: "fixed", inset: 0, background: "#000000cc", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 100 }}>
          <div style={{ background: MID, border: `1px solid ${GOLD}`, borderRadius: 16, padding: 28, width: "100%", maxWidth: 360, textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: GOLD, marginBottom: 20 }}>Muuda reg. nr.</div>
            <div style={{ color: "#888", fontSize: 13, marginBottom: 12 }}>Praegune: <span style={{ color: "#fff", fontWeight: 700 }}>{editingReg.reg}</span></div>
            <input
              value={editRegValue}
              onChange={e => setEditRegValue(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && handleRegUpdate()}
              autoFocus
              style={{ width: "100%", boxSizing: "border-box", background: DARK, border: "1px solid #444", borderRadius: 8, padding: "12px", color: "#fff", fontSize: 20, fontWeight: 800, letterSpacing: 2, fontFamily: "monospace", textAlign: "center", outline: "none", marginBottom: 20 }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setEditingReg(null)}
                style={{ flex: 1, padding: "14px", background: "#333", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                Tühista
              </button>
              <button onClick={handleRegUpdate}
                style={{ flex: 2, padding: "14px", background: GOLD, border: "none", borderRadius: 10, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 15 }}>
                Salvesta
              </button>
            </div>
          </div>
        </div>
      )}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "#000000cc", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: MID, border: `1px solid ${GOLD}`, borderRadius: 16, padding: 28, width: "100%", maxWidth: 400 }}>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 20, color: GOLD }}>Lisa sõiduk</div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: "#888", letterSpacing: 1, marginBottom: 6 }}>REG. NR.</div>
              <input value={newReg} onChange={e => setNewReg(e.target.value.toUpperCase())} placeholder="nt. 123 ABC"
                list="reg-suggestions"
                style={{ width: "100%", boxSizing: "border-box", background: DARK, border: "1px solid #444", borderRadius: 8, padding: "12px", color: "#fff", fontSize: 18, fontWeight: 800, letterSpacing: 2, fontFamily: "monospace" }} />
              <datalist id="reg-suggestions">
                {regSuggestions.map(r => <option key={r} value={r} />)}
              </datalist>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: "#888", letterSpacing: 1, marginBottom: 6 }}>SÕIDUKI TÜÜP</div>
              <div style={{ display: "flex", gap: 8 }}>
                {VEHICLE_TYPES.map(t => (
                  <button key={t} onClick={() => setNewType(t)} style={{ flex: 1, padding: "10px 4px", borderRadius: 8, fontSize: 11, border: `1px solid ${newType === t ? GOLD : "#444"}`, background: newType === t ? "#2a2410" : DARK, color: newType === t ? GOLD : "#888", cursor: "pointer", fontWeight: 700 }}>{t}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: "#888", letterSpacing: 1, marginBottom: 6 }}>MATERJAL</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {materials.map(m => (
                  <button key={m.id} onClick={() => setNewMaterial(m.name)} style={{ padding: "10px 16px", borderRadius: 8, fontSize: 13, border: `1px solid ${newMaterial === m.name ? GOLD : "#444"}`, background: newMaterial === m.name ? "#2a2410" : DARK, color: newMaterial === m.name ? GOLD : "#888", cursor: "pointer", fontWeight: 700 }}>{m.name}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: "14px", background: "#333", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>Tühista</button>
              <button onClick={handleAddTruck} style={{ flex: 2, padding: "14px", background: GOLD, border: "none", borderRadius: 10, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 15 }}>Lisa</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
